import { useEffect, useState } from 'react';
import Modal from '@/components/Modal';
import { Button } from '@/components/Button';
import { apiGetRequest, apiPostRequest } from '@/utils/apiClient';
import { accountDelete } from '@/utils/accountDelete';
import { detectDeviceAndBrowser } from '@/utils/deviceAndBrowser';
import { getAppType } from '@/utils/loginInfo';
import { useTranslation } from 'react-i18next';

const FEEDBACK_CODE = 'WITHDRAWAL';
const DEFAULT_OPTION_KEYS = [
    'accountDelete.feedback.options.missingExpectedFeature',
    'accountDelete.feedback.options.hardToUse',
    'accountDelete.feedback.options.hardToUnderstandAnalytics',
    'accountDelete.feedback.options.foundBetterService',
    'accountDelete.feedback.options.priceValueNotAcceptable',
    'accountDelete.feedback.options.securityPrivacyConcerns',
    'accountDelete.feedback.options.achievedGoals',
    'accountDelete.feedback.options.other',
];

type FeedbackTemplateItems = {
    options?: boolean;
    comment?: boolean;
};

type FeedbackTemplateResponse = {
    feedbackCode: string;
    version: number;
    items?: FeedbackTemplateItems;
};

type WithdrawalFeedbackModalProps = {
    isOpen: boolean;
    onClose: () => void;
    userId: number;
};

type WizardStep = 'select' | 'comment';

const WithdrawalFeedbackModal = ({ isOpen, onClose, userId }: WithdrawalFeedbackModalProps) => {
    const { t, i18n } = useTranslation('settings');
    const [template, setTemplate] = useState<FeedbackTemplateResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [comment, setComment] = useState<string>('');
    const [wizardStep, setWizardStep] = useState<WizardStep>('select');
    const [loadError, setLoadError] = useState<string | null>(null);
    const [stepError, setStepError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const optionsEnabled = template?.items?.options ?? true;
    const commentEnabled = template?.items?.comment ?? true;

    useEffect(() => {
        if (!isOpen) {
            setTemplate(null);
            setSelectedOptions([]);
            setComment('');
            setWizardStep('select');
            setLoadError(null);
            setStepError(null);
            setSubmitError(null);
            setIsSubmitting(false);
            return;
        }

        const loadTemplate = async () => {
            setIsLoading(true);
            setLoadError(null);
            try {
                const localeParam = encodeURIComponent(i18n.language ?? '');
                const response = await apiGetRequest(
                    `/api/feedback/templates/${FEEDBACK_CODE}?locale=${localeParam}`,
                );
                if (!response.ok) {
                    throw new Error('failed to load template');
                }
                const data: FeedbackTemplateResponse = await response.json();
                setTemplate(data);
                const nextOptionsEnabled = data.items?.options ?? true;
                const nextCommentEnabled = data.items?.comment ?? true;
                if (!nextOptionsEnabled) {
                    setSelectedOptions([]);
                    setWizardStep('comment');
                    if (!nextCommentEnabled) {
                        setComment('');
                    }
                }
            } catch (error) {
                setLoadError(t('accountDelete.feedback.loadError'));
            } finally {
                setIsLoading(false);
            }
        };

        void loadTemplate();
    }, [isOpen, t, i18n.language]);

    const toggleOption = (option: string) => {
        if (!optionsEnabled) {
            return;
        }
        setSelectedOptions((prev) => {
            if (prev.includes(option)) {
                return prev.filter((item) => item !== option);
            }
            return [...prev, option];
        });
    };

    const handleProceedToComment = () => {
        if (optionsEnabled && selectedOptions.length === 0) {
            setStepError(t('accountDelete.feedback.validation.optionRequired'));
            return;
        }
        setStepError(null);
        if (commentEnabled) {
            setWizardStep('comment');
        } else {
            void handleSubmit();
        }
    };

    const handleSubmit = async () => {
        setSubmitError(null);
        setIsSubmitting(true);
        try {
            if (template) {
                const selectedOptionPayload = optionsEnabled
                    ? selectedOptions.map((key) => t(key, { defaultValue: key }))
                    : [];
                const response = await apiPostRequest('/api/feedback/submit', {
                    feedbackCode: template.feedbackCode ?? FEEDBACK_CODE,
                    templateVersion: template.version,
                    selectedOptions: selectedOptionPayload,
                    comment: commentEnabled && comment?.trim() ? comment.trim() : null,
                    deviceBrowser: detectDeviceAndBrowser(),
                    appType: getAppType(),
                    locale: i18n.language,
                });
                if (!response.ok) {
                    throw new Error('feedback submit failed');
                }
            }
            await accountDelete(userId);
        } catch (error) {
            setSubmitError(t('accountDelete.feedback.submitError'));
            setIsSubmitting(false);
        }
    };

    const handleSkipFeedback = async () => {
        setSubmitError(null);
        setIsSubmitting(true);
        try {
            await accountDelete(userId);
        } catch (error) {
            setSubmitError(t('accountDelete.feedback.submitError'));
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting) return;
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            contentClassName="w-full max-w-3xl p-8 min-h-[520px]"
            disableCloseOnEsc={isSubmitting}
            disableCloseOnOverlay={isSubmitting}
        >
            <div className="pt-2 pb-2">
                {/* タイトルと説明 */}
                <h2 className="text-xl text-center font-semibold mb-8">
                    {t('accountDelete.feedback.title')}
                </h2>
                {/* 読み込み画面 */}
                {isLoading && (
                    <p className="text-sm text-gray-500">{t('accountDelete.feedback.loading')}</p>
                )}
                {/* エラー画面 */}
                {!isLoading && loadError && (
                    <div className="space-y-4">
                        <p className="text-sm text-red-600">{loadError}</p>
                        <div className="flex justify-center gap-4">
                            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                                {t('accountDelete.feedback.cancel')}
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleSkipFeedback}
                                isLoading={isSubmitting}
                                loadingLabel={t('accountDelete.feedback.processing')}
                                className="px-6 w-auto"
                            >
                                {t('accountDelete.feedback.withdrawButton')}
                            </Button>
                        </div>
                    </div>
                )}
                {/* 選択画面 */}
                {!isLoading &&
                    !loadError &&
                    wizardStep === 'select' &&
                    template &&
                    optionsEnabled && (
                        <div className="space-y-6 min-h-[300px] flex flex-col">
                            <div>
                                <p className="text-sm text-center font-medium mb-3">
                                    {t('accountDelete.feedback.reasonLabel')}
                                </p>
                                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                    {DEFAULT_OPTION_KEYS.map((optionKey) => {
                                        const label = t(optionKey, { defaultValue: optionKey });
                                        return (
                                            <label
                                                key={optionKey}
                                                className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-400 transition cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="mt-1 h-4 w-4"
                                                    checked={selectedOptions.includes(optionKey)}
                                                    onChange={() => toggleOption(optionKey)}
                                                />
                                                <span className="text-sm text-gray-700">
                                                    {label}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {stepError && (
                                    <p className="text-sm text-red-600 mt-3">{stepError}</p>
                                )}
                            </div>
                            {/* ボタン */}
                            <div className="mt-auto flex justify-center gap-8">
                                <Button
                                    variant="outline"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className="px-6 w-auto"
                                >
                                    {t('accountDelete.feedback.cancel')}
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleProceedToComment}
                                    className="px-6 w-auto"
                                >
                                    {t('accountDelete.feedback.sendButton')}
                                </Button>
                            </div>
                        </div>
                    )}

                {/* コメント入力画面 */}
                {!isLoading && !loadError && wizardStep === 'comment' && (
                    <div className="space-y-6 min-h-[300px] flex flex-col gap-6">
                        {commentEnabled && (
                            <div>
                                <label
                                    htmlFor="withdrawal-feedback-comment"
                                    className="block text-sm font-medium mb-2"
                                >
                                    {t('accountDelete.feedback.commentLabel')}
                                </label>
                                <textarea
                                    id="withdrawal-feedback-comment"
                                    className="w-full min-h-[120px] border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    value={comment}
                                    onChange={(event) => setComment(event.target.value)}
                                    placeholder={t('accountDelete.feedback.commentPlaceholder')}
                                    disabled={isSubmitting}
                                />
                            </div>
                        )}
                        {submitError && <p className="text-sm text-red-600">{submitError}</p>}
                        <div className="mt-auto flex justify-center gap-8">
                            <Button
                                variant="outline"
                                onClick={handleClose}
                                disabled={isSubmitting}
                                className="px-6 w-auto"
                            >
                                {t('accountDelete.feedback.cancel')}
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleSubmit}
                                isLoading={isSubmitting}
                                loadingLabel={t('accountDelete.feedback.processing')}
                                className="px-6 w-auto"
                            >
                                {t('accountDelete.feedback.withdrawButton')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default WithdrawalFeedbackModal;
